import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { Personne } from "models";
import ElusList from "components/Informations/Elus/ElusList";
import ElusListSkeleton from "components/Informations/Elus/ElusListSkeleton";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { mandatsService } from "services";
import { elusUtil } from "utils";
import type Fonctions from "constants/Fonctions";
import { FonctionNames } from "constants/Fonctions";
import { Grid, Typography } from "@mui/material";
import { Masonry } from "@mui/lab";

interface FilteredElusListProps {
  fonctions: Array<string> | string;
  territoireId: string;
  /** Affiche un sous-titre pour chaque fonction avec une liste des élus correspondants */
  sousTitresFonctions?: boolean;
}

function InnerFilteredElusList({
  fonctions,
  territoireId,
  sousTitresFonctions = false,
}: Readonly<FilteredElusListProps>): ReactNode {
  const [elus, setElus] = useState<Array<Personne>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    if (fonctions.length !== 0) {
      void catchErrors(async () => {
        const _mandats = await mandatsService.getByFilter({
          territoiresIds: [territoireId],
          fonctionsIds: Array.isArray(fonctions) ? fonctions : [fonctions],
          isActive: true,
        });

        setElus(elusUtil.getElusFromMandats(_mandats).toSorted(elusUtil.sortByName));
      });
    }
  }, [fonctions, territoireId, catchErrors]);

  if (isLoading) {
    return <ElusListSkeleton />;
  }

  if (sousTitresFonctions) {
    return (
      <Masonry columns={{ xs: 1, lg: 2 }} spacing={2}>
        {(Array.isArray(fonctions) ? fonctions : [fonctions]).map((fonction): ReactNode => {
          const filteredElus = elus.filter(
            (elu) => elu.mandats?.some((mandat) => mandat.idFonction === fonction) ?? false
          );
          return (
            <Grid key={fonction} size={{ xs: 12, lg: 6 }}>
              <Typography variant="h6" gutterBottom>
                {FonctionNames[fonction as Fonctions]?.P ?? FonctionNames[fonction as Fonctions]?.P}
              </Typography>
              {filteredElus.length === 0 ? (
                <Typography color="grey">Aucun</Typography>
              ) : (
                <ElusList elus={filteredElus} />
              )}
            </Grid>
          );
        })}
      </Masonry>
    );
  }

  return <ElusList elus={elus} />;
}

const FilteredElusList = withComponentErrorBoundary(InnerFilteredElusList);
export default FilteredElusList;
